import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, query } from '../api.js'
import Layout from '../componentes/Layout.jsx'
import {
  useDados, Campo, Erro, Aviso, Carregando, EstadoVazio, emReais, emNumero, emData, emKwh,
  emDataSimples, paraCampoDataHora, NOME_PROPULSAO, mesmoTexto,
} from '../componentes/basicos.jsx'

const ABAS = [
  { chave: 'veiculos', rotulo: 'Veículos' },
  { chave: 'pontos', rotulo: 'Pontos' },
]

export default function Garagem() {
  const [aba, setAba] = useState('veiculos')

  return (
    <Layout titulo="Garagem">
      <div className="chips" role="tablist" aria-label="Seções">
        {ABAS.map((a) => (
          <button key={a.chave} type="button" className="chip" role="tab"
                  aria-pressed={aba === a.chave} onClick={() => setAba(a.chave)}>
            {a.rotulo}
          </button>
        ))}
      </div>

      {aba === 'veiculos' && <Veiculos />}
      {aba === 'pontos' && <Pontos />}
    </Layout>
  )
}

// ------------------------------------------------------------------- veículos

const COMBUSTIVEIS = ['gasolina', 'etanol', 'diesel', 'gnv']

function Veiculos() {
  const { dados, carregando, recarregar } = useDados('/veiculos?incluir_inativos=true')
  const [editando, setEditando] = useState(null)

  if (carregando) return <Carregando />

  if (dados.veiculos.length === 0) {
    return (
      <EstadoVazio titulo="Nenhum veículo cadastrado">
        <p className="dica">Comece cadastrando o veículo que você usa no dia a dia.</p>
        <Link className="botao botao-principal" to="/veiculos/novo">Cadastrar veículo</Link>
      </EstadoVazio>
    )
  }

  return (
    <div className="pilha">
      <div className="linha">
        <Link className="botao botao-principal" to="/veiculos/novo">Cadastrar novo veículo</Link>
      </div>

      <section className="cartao">
        <ul className="lista">
          {dados.veiculos.map((v) => (
            <li key={v.id}>
              <div className="entre">
                <div>
                  <div className="titulo-item">{v.nome}</div>
                  <div className="detalhe">
                    {NOME_PROPULSAO[v.propulsao]}
                    {v.combustivel && ` · ${v.combustivel}`}
                    {` · odômetro inicial ${emNumero(v.odometro_inicial, 0)} km`}
                    {!v.ativo && ' · inativo'}
                  </div>
                </div>

                <div className="linha" style={{ flexWrap: 'nowrap' }}>
                  <Link className="botao botao-fantasma" to={`/veiculos/${v.id}`}>Detalhes</Link>

                  <button type="button" className="botao-fantasma"
                          onClick={() => setEditando(editando === v.id ? null : v.id)}>
                    {editando === v.id ? 'Fechar' : 'Editar'}
                  </button>

                  <button type="button" className="botao-fantasma"
                          onClick={async () => {
                            await api.patch(`/veiculos/${v.id}`, { ativo: v.ativo ? 0 : 1 })
                            recarregar()
                          }}>
                    {v.ativo ? 'Desativar' : 'Reativar'}
                  </button>
                </div>
              </div>

              {editando === v.id && (
                <>
                  <EditarVeiculo veiculo={v}
                                 aoSalvar={async () => { setEditando(null); await recarregar() }} />
                  <ExcluirVeiculo veiculo={v}
                                  aoExcluir={async () => { setEditando(null); await recarregar() }} />
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/**
 * Edição do veículo. A propulsão não aparece: ela define como todo o histórico foi
 * calculado, então mudá-la invalidaria os lançamentos existentes — para trocar,
 * cadastre outro veículo.
 */
function EditarVeiculo({ veiculo, aoSalvar }) {
  const [form, setForm] = useState({
    nome: veiculo.nome,
    combustivel: veiculo.combustivel ?? '',
    odometro_inicial: String(veiculo.odometro_inicial),
    capacidade_bateria_kwh: veiculo.capacidade_bateria_kwh ?? '',
    capacidade_tanque_l: veiculo.capacidade_tanque_l ?? '',
  })
  const [erro, setErro] = useState(null)
  const [salvando, setSalvando] = useState(false)

  const temBateria = veiculo.propulsao === 'eletrico' || veiculo.propulsao === 'phev'
  const temTanque = veiculo.propulsao !== 'eletrico'
  const numero = (t) => (t === '' ? null : Number(String(t).replace(',', '.')))

  async function salvar(evento) {
    evento.preventDefault()
    setSalvando(true)
    setErro(null)
    try {
      await api.patch(`/veiculos/${veiculo.id}`, {
        nome: form.nome,
        odometro_inicial: Number(form.odometro_inicial),
        ...(temBateria ? { capacidade_bateria_kwh: numero(form.capacidade_bateria_kwh) } : {}),
        ...(temTanque ? { capacidade_tanque_l: numero(form.capacidade_tanque_l) } : {}),
        // Só manda o combustível se mudou: o servidor recusa a troca quando já há
        // histórico com outra energia.
        ...(form.combustivel !== (veiculo.combustivel ?? '')
          ? { combustivel: form.combustivel || null }
          : {}),
      })
      await aoSalvar()
    } catch (e) {
      setErro(e)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <form className="pilha" style={{ marginTop: 12 }} onSubmit={salvar}>
      <Campo rotulo="Nome">
        <input value={form.nome} required onChange={(e) => setForm({ ...form, nome: e.target.value })} />
      </Campo>

      <Campo rotulo="Odômetro inicial (km)"
             dica="O ponto de partida das contas. Use 0 se o veículo foi comprado novo e você quer contar desde o começo.">
        <input inputMode="numeric" value={form.odometro_inicial} required
               onChange={(e) => setForm({ ...form, odometro_inicial: e.target.value })} />
      </Campo>

      {(veiculo.propulsao === 'combustao' || veiculo.propulsao === 'phev') && (
        <Campo rotulo="Combustível">
          <select value={form.combustivel}
                  onChange={(e) => setForm({ ...form, combustivel: e.target.value })}>
            <option value="">Não definido</option>
            {COMBUSTIVEIS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Campo>
      )}

      {temBateria && (
        <Campo rotulo="Capacidade da bateria (kWh)">
          <input inputMode="decimal" value={form.capacidade_bateria_kwh}
                 onChange={(e) => setForm({ ...form, capacidade_bateria_kwh: e.target.value })} />
        </Campo>
      )}

      {temTanque && (
        <Campo rotulo="Capacidade do tanque (L)">
          <input inputMode="decimal" value={form.capacidade_tanque_l}
                 onChange={(e) => setForm({ ...form, capacidade_tanque_l: e.target.value })} />
        </Campo>
      )}

      <Erro erro={erro} />

      <button type="submit" className="botao-principal botao-largo" disabled={salvando}>
        {salvando ? 'Salvando…' : 'Salvar alterações'}
      </button>
    </form>
  )
}

/**
 * Exclusão definitiva. Fica atrás da edição e pede o nome digitado, como a
 * destruição de conta na área do admin: some com anos de histórico e não há
 * como desfazer. Quem só quer tirar da lista usa "Desativar".
 */
function ExcluirVeiculo({ veiculo, aoExcluir }) {
  const [aberto, setAberto] = useState(false)
  const [confirmacao, setConfirmacao] = useState('')
  const [erro, setErro] = useState(null)
  const { dados: dependentes } = useDados(
    aberto ? `/veiculos/${veiculo.id}/dependentes` : null, [aberto, veiculo.id],
  )

  const total = dependentes
    ? Object.values(dependentes).reduce((s, n) => s + n, 0)
    : null

  if (!aberto) {
    return (
      <button type="button" className="botao-perigo" style={{ marginTop: 8 }}
              onClick={() => setAberto(true)}>
        Excluir veículo
      </button>
    )
  }

  return (
    <div className="pilha" style={{ marginTop: 12 }}>
      <Aviso tipo="erro">
        {total === null && 'Verificando o que será apagado…'}
        {total === 0 && `Isto apaga "${veiculo.nome}". Não há como desfazer.`}
        {total > 0 && (
          <>
            Isto apaga <strong>{veiculo.nome}</strong> e todo o histórico dele:{' '}
            {dependentes.abastecimentos} abastecimento(s), {dependentes.manutencoes} manutenção(ões){' '}
            e {dependentes.despesas} despesa(s). Não há como desfazer.
            <br />
            Se você só quer tirar o veículo da lista, use <strong>Desativar</strong>.
          </>
        )}
      </Aviso>

      <Campo rotulo={`Digite "${veiculo.nome}" para confirmar`}>
        <input value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} />
      </Campo>

      <Erro erro={erro} />

      <div className="linha">
        <button type="button" className="botao-perigo"
                disabled={!mesmoTexto(confirmacao, veiculo.nome)}
                onClick={async () => {
                  setErro(null)
                  try {
                    await api.remover(`/veiculos/${veiculo.id}`, { confirmacao_nome: confirmacao })
                    await aoExcluir()
                  } catch (e) {
                    setErro(e)
                  }
                }}>
          Excluir definitivamente
        </button>
        <button type="button" className="botao-fantasma" onClick={() => setAberto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------- pontos

const TIPOS_PONTO = [
  { valor: 'residencial', rotulo: 'Casa (conta de luz)' },
  { valor: 'eletroposto', rotulo: 'Eletroposto' },
  { valor: 'posto', rotulo: 'Posto de combustível' },
]

/**
 * Relógio de medição próprio do ponto (SPEC §5.8), no estado do formulário.
 * `desde` é o valor de um <input type="datetime-local">, em hora local.
 */
const SEM_MEDIDOR = { ligado: false, leitura: '', desde: '' }

const medidorDoPonto = (ponto) => (ponto.medidor
  ? { ligado: true, leitura: String(ponto.medidor.leitura_inicial), desde: paraCampoDataHora(ponto.medidor.desde) }
  : SEM_MEDIDOR)

function medidorParaApi(medidor, desdeAtual = null) {
  if (!medidor.ligado) return null
  return {
    leitura_inicial: Number(String(medidor.leitura).replace(',', '.')),
    // Data intocada vai como estava: o campo só tem minutos, e devolvê-la por ele
    // mexeria nos segundos a cada edição. "Agora" sai do relógio deste aparelho, o
    // mesmo que carimba as recargas — com o do servidor, uma diferença de segundos
    // deixaria a primeira recarga "antes" de o relógio existir.
    desde: desdeAtual && medidor.desde === paraCampoDataHora(desdeAtual)
      ? desdeAtual
      : new Date(medidor.desde || Date.now()).toISOString(),
  }
}

function CamposMedidor({ medidor, aoMudar }) {
  return (
    <>
      <label className="linha" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        <input type="checkbox" style={{ width: 20, minHeight: 20, marginTop: 2, flexShrink: 0 }}
               checked={medidor.ligado}
               onChange={(e) => aoMudar({ ...medidor, ligado: e.target.checked })} />
        <span>
          <strong>Relógio de medição próprio</strong>
          <span className="dica" style={{ display: 'block' }}>
            Para quem anota o relógio do carregador: em cada recarga você informa em quanto
            ele parou, e o sistema calcula os kWh pela diferença.
          </span>
        </span>
      </label>

      {medidor.ligado && (
        <>
          <Campo rotulo="Em quanto o relógio está (kWh)"
                 dica="A leitura ao final da última recarga feita aqui.">
            <input inputMode="decimal" value={medidor.leitura} required
                   onChange={(e) => aoMudar({ ...medidor, leitura: e.target.value })} />
          </Campo>

          <Campo rotulo="Leitura feita em"
                 dica="Vazio = agora. Recargas a partir daqui são lançadas pelo relógio; as anteriores ficam como estão.">
            <input type="datetime-local" value={medidor.desde}
                   onChange={(e) => aoMudar({ ...medidor, desde: e.target.value })} />
          </Campo>
        </>
      )}
    </>
  )
}

function Pontos() {
  const { dados, carregando, recarregar } = useDados('/pontos?incluir_inativos=true')
  const [form, setForm] = useState({ nome: '', tipo: 'residencial', gratuito: false })
  const [medidor, setMedidor] = useState(SEM_MEDIDOR)
  const [erro, setErro] = useState(null)
  const [editando, setEditando] = useState(null)
  const [conta, setConta] = useState(null)

  async function criar(evento) {
    evento.preventDefault()
    setErro(null)
    try {
      await api.post('/pontos', {
        ...form,
        ...(form.tipo !== 'posto' && medidor.ligado ? { medidor: medidorParaApi(medidor) } : {}),
      })
      setForm({ nome: '', tipo: 'residencial', gratuito: false })
      setMedidor(SEM_MEDIDOR)
      await recarregar()
    } catch (e) {
      setErro(e)
    }
  }

  if (carregando) return <Carregando />

  return (
    <div className="colunas-desktop">
      {dados.pontos.length > 0 && (
        <section className="cartao">
          <ul className="lista">
            {dados.pontos.map((p) => (
              <li key={p.id}>
                <div className="entre">
                  <div>
                    <div className="titulo-item">{p.nome}</div>
                    <div className="detalhe">
                      {TIPOS_PONTO.find((t) => t.valor === p.tipo)?.rotulo}
                      {p.gratuito && ' · gratuito'}
                      {!p.ativo && ' · inativo'}
                    </div>
                    {p.medidor && (
                      <div className="detalhe">
                        Relógio em {emKwh(p.medidor.ultima_leitura)} · {emData(p.medidor.ultima_leitura_em)}
                      </div>
                    )}
                  </div>
                  <div className="linha" style={{ flexWrap: 'nowrap' }}>
                    {/* A conta de luz é do ponto: mora aqui dentro, não numa aba
                        separada que fingia valer para o sistema todo. */}
                    {p.tipo === 'residencial' && (
                      <button type="button" className="botao-fantasma"
                              onClick={() => setConta(conta === p.id ? null : p.id)}>
                        {conta === p.id ? 'Fechar conta' : 'Conta de luz'}
                      </button>
                    )}
                    <button type="button" className="botao-fantasma"
                            onClick={() => setEditando(editando === p.id ? null : p.id)}>
                      {editando === p.id ? 'Fechar' : 'Editar'}
                    </button>
                    <button type="button" className="botao-fantasma"
                            onClick={async () => {
                              await api.patch(`/pontos/${p.id}`, { ativo: p.ativo ? 0 : 1 })
                              recarregar()
                            }}>
                      {p.ativo ? 'Desativar' : 'Reativar'}
                    </button>
                  </div>
                </div>

                {editando === p.id && (
                  <EditarPonto ponto={p}
                               aoSalvar={async () => { setEditando(null); await recarregar() }} />
                )}

                {conta === p.id && <ContaDeLuz ponto={p} />}
              </li>
            ))}
          </ul>
        </section>
      )}

      <form className="cartao pilha" onSubmit={criar}>
        <span className="rotulo-secao">Novo ponto</span>

        <Campo rotulo="Nome">
          <input value={form.nome} required onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        </Campo>

        <Campo rotulo="Tipo">
          <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
            {TIPOS_PONTO.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
          </select>
        </Campo>

        <label className="linha" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 20, minHeight: 20 }} checked={form.gratuito}
                 onChange={(e) => setForm({ ...form, gratuito: e.target.checked })} />
          <span>Recarga gratuita</span>
        </label>

        {form.tipo !== 'posto' && <CamposMedidor medidor={medidor} aoMudar={setMedidor} />}

        <Erro erro={erro} />
        <button type="submit" className="botao-principal botao-largo">Cadastrar ponto</button>
      </form>
    </div>
  )
}

function EditarPonto({ ponto, aoSalvar }) {
  const [form, setForm] = useState({
    nome: ponto.nome, tipo: ponto.tipo, gratuito: ponto.gratuito,
  })
  const [medidor, setMedidor] = useState(() => medidorDoPonto(ponto))
  const [erro, setErro] = useState(null)
  const [salvando, setSalvando] = useState(false)

  // Virar posto desliga o relógio: bomba de combustível não tem leitura de energia.
  const medidorFinal = form.tipo === 'posto' ? SEM_MEDIDOR : medidor
  // Só manda o relógio quando ele mudou: reenviar a mesma leitura religaria a
  // primeira recarga medida sem ninguém ter pedido.
  const inicial = medidorDoPonto(ponto)
  const medidorMudou = medidorFinal.ligado !== inicial.ligado
    || (medidorFinal.ligado && (medidorFinal.leitura !== inicial.leitura || medidorFinal.desde !== inicial.desde))

  async function salvar(evento) {
    evento.preventDefault()
    setSalvando(true)
    setErro(null)
    try {
      await api.patch(`/pontos/${ponto.id}`, {
        nome: form.nome, tipo: form.tipo, gratuito: form.gratuito ? 1 : 0,
        ...(medidorMudou ? { medidor: medidorParaApi(medidorFinal, ponto.medidor?.desde) } : {}),
      })
      await aoSalvar()
    } catch (e) {
      setErro(e)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <form className="pilha" style={{ marginTop: 12 }} onSubmit={salvar}>
      <Campo rotulo="Nome">
        <input value={form.nome} required onChange={(e) => setForm({ ...form, nome: e.target.value })} />
      </Campo>

      <Campo rotulo="Tipo"
             dica="Só o tipo residencial usa a tarifa da conta de luz; nos outros o valor é informado a cada abastecimento.">
        <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
          {TIPOS_PONTO.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
        </select>
      </Campo>

      <label className="linha" style={{ gap: 8 }}>
        <input type="checkbox" style={{ width: 20, minHeight: 20 }} checked={form.gratuito}
               onChange={(e) => setForm({ ...form, gratuito: e.target.checked })} />
        <span>Recarga gratuita</span>
      </label>

      {form.tipo !== 'posto' && <CamposMedidor medidor={medidor} aoMudar={setMedidor} />}

      {ponto.medidor && !medidorFinal.ligado && (
        <Aviso tipo="atencao">
          Desligar o relógio mantém os kWh das recargas já lançadas. As próximas voltam a
          pedir os kWh.
        </Aviso>
      )}

      <Erro erro={erro} />
      <button type="submit" className="botao-principal botao-largo" disabled={salvando}>
        {salvando ? 'Salvando…' : 'Salvar alterações'}
      </button>
    </form>
  )
}

// -------------------------------------------------------------------- tarifas

/**
 * Conta de luz de **um** ponto, aberta de dentro dele.
 *
 * Ficava numa aba própria, o que dava a impressão de uma tarifa única do sistema.
 * A tarifa sempre foi por ponto (`ponto_id` obrigatório no banco): duas casas têm
 * duas contas, com vigências independentes. Aqui a tela reflete o modelo.
 */
function ContaDeLuz({ ponto }) {
  const { dados, recarregar } = useDados(`/tarifas${query({ ponto_id: ponto.id })}`, [ponto.id])
  const [form, setForm] = useState({ vigencia_inicio: '', valor_kwh: '', bandeira: '' })
  const [erro, setErro] = useState(null)
  const [resultado, setResultado] = useState(null)

  async function lancarFatura(evento) {
    evento.preventDefault()
    setErro(null)
    setResultado(null)
    try {
      const r = await api.post('/tarifas', {
        ponto_id: ponto.id,
        vigencia_inicio: form.vigencia_inicio,
        valor_kwh: Number(String(form.valor_kwh).replace(',', '.')),
        bandeira: form.bandeira || null,
      })
      setResultado(r.recalculo)
      setForm({ vigencia_inicio: '', valor_kwh: '', bandeira: '' })
      await recarregar()
    } catch (e) {
      setErro(e)
    }
  }

  const vigencias = dados?.tarifas ?? []

  return (
    <div className="pilha" style={{ marginTop: 12 }}>
      <form className="pilha" onSubmit={lancarFatura}>
        <span className="rotulo-secao">Lançar fatura · {ponto.nome}</span>
        <p className="dica">
          Informe só o início de cada fatura — o fim é sempre o dia anterior ao início da
          seguinte, e o sistema encaixa isso sozinho. Pode lançar faturas antigas em
          qualquer ordem; as recargas feitas aqui são recalculadas.
        </p>

        <Campo rotulo="Início da vigência">
          <input type="date" value={form.vigencia_inicio} required
                 onChange={(e) => setForm({ ...form, vigencia_inicio: e.target.value })} />
        </Campo>

        <Campo rotulo="Valor do kWh (R$)" dica="Use o valor final da conta, com impostos.">
          <input inputMode="decimal" value={form.valor_kwh} required placeholder="0,89"
                 onChange={(e) => setForm({ ...form, valor_kwh: e.target.value })} />
        </Campo>

        <Campo rotulo="Bandeira">
          <input value={form.bandeira} placeholder="verde, amarela…"
                 onChange={(e) => setForm({ ...form, bandeira: e.target.value })} />
        </Campo>

        <Erro erro={erro} />

        {resultado && (
          <Aviso tipo="ok">
            {resultado.abastecimentos_recalculados} recarga(s) recalculada(s) ·
            variação de {emReais(resultado.variacao_total)}.
          </Aviso>
        )}

        <button type="submit" className="botao-principal botao-largo">Registrar fatura</button>
      </form>

      {vigencias.length > 0 && (
        <div className="pilha">
          <span className="rotulo-secao">Vigências</span>
          <ul className="lista">
            {vigencias.map((t) => (
              <li key={t.id}>
                <div className="titulo-item">
                  {emReais(t.valor_kwh)} / kWh {t.atual && '· atual'}
                </div>
                <div className="detalhe">
                  desde {emDataSimples(t.vigencia_inicio)}
                  {t.vigencia_fim ? ` até ${emDataSimples(t.vigencia_fim)}` : ''}
                  {t.bandeira ? ` · bandeira ${t.bandeira}` : ''}
                </div>
                <CorrigirVigencia tarifa={t} aoSalvar={recarregar} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/** Correção de digitação — edita a vigência sem criar outra (SPEC §5.1.6). */
function CorrigirVigencia({ tarifa, aoSalvar }) {
  const [aberto, setAberto] = useState(false)
  const [valor, setValor] = useState(String(tarifa.valor_kwh))
  const [inicio, setInicio] = useState(tarifa.vigencia_inicio)
  const [erro, setErro] = useState(null)
  const [resultado, setResultado] = useState(null)

  async function corrigir(evento) {
    evento.preventDefault()
    setErro(null)
    try {
      const r = await api.patch(`/tarifas/${tarifa.id}/corrigir`, {
        valor_kwh: Number(String(valor).replace(',', '.')),
        vigencia_inicio: inicio,
      })
      setResultado(r.recalculo)
      await aoSalvar()
    } catch (e) {
      setErro(e)
    }
  }

  if (!aberto) {
    return (
      <button type="button" className="botao-fantasma" style={{ marginTop: 8 }}
              onClick={() => setAberto(true)}>
        Corrigir
      </button>
    )
  }

  return (
    <form className="pilha" style={{ marginTop: 8 }} onSubmit={corrigir}>
      <Campo rotulo="Início da vigência"
             dica="Mudar esta data reencaixa a fatura anterior automaticamente.">
        <input type="date" value={inicio} required onChange={(e) => setInicio(e.target.value)} />
      </Campo>

      <Campo rotulo="Valor do kWh (R$)" dica="Corrige a fatura no lugar, sem criar outra.">
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />
      </Campo>

      <Erro erro={erro} />
      {resultado && (
        <Aviso tipo="ok">
          {resultado.abastecimentos_recalculados} recarga(s) · {emReais(resultado.variacao_total)}.
        </Aviso>
      )}
      <div className="linha">
        <button type="submit" className="botao-principal">Salvar correção</button>
        <button type="button" className="botao-fantasma" onClick={() => setAberto(false)}>Cancelar</button>
      </div>
    </form>
  )
}
